import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { UserPlus, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { AuthShell, Field, RememberMeCheckbox } from "./Login";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const goToGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Register() {
    const nav = useNavigate();
    const { register } = useAuth();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (password.length < 6) {
            setErr("La password deve avere almeno 6 caratteri");
            return;
        }
        setBusy(true);
        try {
            const { migrated } = await register(email.trim(), password, name.trim(), { rememberMe });
            toast.success("Account creato");
            if (migrated > 0) {
                toast.info(`Migrati ${migrated} dati dal profilo anonimo al tuo account`);
            }
            nav("/", { replace: true });
        } catch (ex) {
            const msg = formatApiErrorDetail(ex?.response?.data?.detail) || ex.message;
            setErr(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell title="Registrati" subtitle="Crea il tuo profilo Capitaly">
            <button
                data-testid="google-signup-btn"
                type="button"
                onClick={goToGoogle}
                className="w-full flex items-center justify-center gap-2 border border-soft bg-elev2 px-4 py-2.5 text-sm font-semibold mb-4 hover:bg-elev"
            >
                <GoogleIcon /> Registrati con Google
            </button>
            <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-soft" />
                <span className="text-[10px] text-mute tracking-widest">OPPURE</span>
                <div className="flex-1 h-px bg-soft" />
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="register-form">
                <Field icon={User} type="text" placeholder="Il tuo nome" value={name} onChange={setName} testid="name" autoComplete="name" />
                <Field icon={Mail} type="email" placeholder="email@capitaly.app" value={email} onChange={setEmail} testid="email" autoComplete="email" />
                <Field icon={Lock} type="password" placeholder="Password (min 6 caratteri)" value={password} onChange={setPassword} testid="password" autoComplete="new-password" />
                <RememberMeCheckbox checked={rememberMe} onChange={setRememberMe} testid="register-remember" />
                {err && <div data-testid="register-error" className="text-[11px] px-2 py-1.5 border" style={{ color: "var(--red)", borderColor: "var(--red)" }}>{err}</div>}
                <button
                    data-testid="register-submit"
                    type="submit"
                    disabled={busy}
                    className="w-full px-4 py-2.5 text-sm font-semibold tracking-wider disabled:opacity-50"
                    style={{ background: "var(--brand-green)", color: "#000" }}
                >
                    {busy ? "Creazione in corso…" : <><UserPlus size={14} className="inline mr-2" />Crea account</>}
                </button>
            </form>
            <div className="text-center text-xs text-mute mt-4">
                Hai già un account?{" "}
                <Link data-testid="link-login" to="/login" style={{ color: "var(--brand-blue)" }} className="font-semibold hover:underline">
                    Accedi
                </Link>
            </div>
        </AuthShell>
    );
}

const GoogleIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09A6.97 6.97 0 015.46 12c0-.73.13-1.43.36-2.09V7.07H2.18A11 11 0 001 12c0 1.78.43 3.46 1.18 4.93l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
);
